import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { EmailIssue } from '@models/email/email-issue';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EmailCreateTabManagerService } from '@services/email-create-tab-manager.service';
import { EmailHistoryService } from '@services/email-history.service';
import { EmailSendingService } from '@services/email-sending.service';
import { checkTimeFormat } from '@utils/validators';
import moment from 'moment';
import Hashids from 'hashids';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'wsb-email-create-summary',
  templateUrl: './email-create-summary.component.html',
  styleUrls: ['./email-create-summary.component.scss'],
  standalone: false
})
export class EmailCreateSummaryComponent implements OnInit, OnDestroy {
  @ViewChild('modalEmailPreview') modalEmailPreview;
  @ViewChild('modalEditTextversion') modalEditTextversion;
  @ViewChild('modalConfirmSend') modalConfirmSend;
  @ViewChild('modalConfirmNavigation') modalConfirmNavigation;
  @Input() emailCampaignIssue: EmailIssue;
  @Output() enableSendButton: EventEmitter<any> = new EventEmitter();
  modalConfirmNavigationModal: NgbModalRef;
  modalEditTextversionModal: NgbModalRef;
  modalEmailPreviewModal: NgbModalRef;
  modalConfirmSendModal: NgbModalRef;

  textVersion: UntypedFormControl = new UntypedFormControl();
  scheduleTime: UntypedFormControl = new UntypedFormControl(false);
  scheduledForm: UntypedFormGroup;

  listenerNext: Subscription;
  hashids;
  selectedEmailPreview;
  size;
  duration;
  today = new Date();

  constructor(
    private emailSendiService: EmailSendingService,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private fb: UntypedFormBuilder,
    private router: Router,
    private emailCreateTabManagerService: EmailCreateTabManagerService,
    private emailHistoryService: EmailHistoryService
  ) {}

  ngOnInit(): void {
    this.buildForm();

    this.isScheduled();

    this.isFormValid();

    this.enableSendButton.emit(true);

    this.getEmailSize();

    this.getEmailIssueCampaign();

    this.listenToNextButton();
    // this.listenToBackButton();
  }

  getEmailIssueCampaign() {
    this.emailHistoryService.getEmailCampaignIssue(this.emailCampaignIssue.idCampaignIssue).subscribe(data => {
      this.emailCampaignIssue = data;
    });
  }

  listenToNextButton() {
    this.listenerNext = this.emailCreateTabManagerService.lgNext$.subscribe(tab => {
      if (tab === 5) {
        this.openSendConfemationModal();
      }
    });
  }

  // listenToBackButton() {
  //   this.emailCreateTabManagerService.lgBack$.subscribe(tab => {
  //     if (tab === 5) {
  //       this.emailCreateTabManagerService.lgDone('back');
  //     }
  //   });
  // }

  buildForm() {
    this.scheduledForm = this.fb.group({
      sendDate: [''],
      sendTime: ['']
    });
  }

  getEmailSize() {
    this.emailSendiService.calculateSizeAndTimeEmail(this.emailCampaignIssue.idCampaignIssue).subscribe(data => {
      this.size = data.size;
      this.duration = data.duration;
    });
  }

  openEditTextversionModal() {
    this.modalEditTextversionModal = this.modalService.open(this.modalEditTextversion);

    this.modalEditTextversionModal.result.then(
      result => {},
      reason => {}
    );
  }

  isScheduled() {
    this.scheduleTime.valueChanges.subscribe(value => {
      if (value) {
        this.scheduledForm.get('sendDate').setValidators(Validators.required);
        this.scheduledForm.get('sendTime').setValidators([Validators.required, checkTimeFormat()]);
        this.scheduledForm.get('sendDate').updateValueAndValidity();
        this.scheduledForm.get('sendTime').updateValueAndValidity();
      } else {
        this.scheduledForm.get('sendDate').setValue('');
        this.scheduledForm.get('sendTime').setValue('');
        this.scheduledForm.get('sendDate').clearValidators();
        this.scheduledForm.get('sendTime').clearValidators();
        this.scheduledForm.get('sendDate').updateValueAndValidity();
        this.scheduledForm.get('sendTime').updateValueAndValidity();
      }
    });
  }

  isFormValid() {
    this.scheduledForm.valueChanges.subscribe(() => {
      if (this.scheduledForm.valid) {
        return this.enableSendButton.emit(true);
      }
      return this.enableSendButton.emit(false);
    });
  }

  saveNewText() {
    const body = {
      messageText: this.textVersion.value
    };
    this.emailSendiService.saveBodyHtml(this.emailCampaignIssue.idCampaignIssue, body).subscribe(data => {
      this.modalEditTextversionModal.close();
      this.getEmailSize();
    });
  }

  openPreviewModal() {
    this.emailHistoryService.getEmailPreview(this.emailCampaignIssue.idCampaignIssue).subscribe(issue => {
      this.selectedEmailPreview = issue.htmlBody;
      this.modalEmailPreviewModal = this.modalService.open(this.modalEmailPreview, { size: 'xl' });

      this.modalEmailPreviewModal.result.then(
        result => {},
        reason => {}
      );
    });
  }

  openSendConfemationModal() {
    this.modalConfirmSendModal = this.modalService.open(this.modalConfirmSend);

    this.modalConfirmSendModal.result.then(
      result => {},
      reason => {}
    );
  }

  goToRecipients() {
    this.emailCreateTabManagerService.lgDone('recipients');
  }

  goToShop() {
    this.router.navigate(['shop/index']);
  }

  sendEmailCampaign() {
    this.modalConfirmSendModal.close();
    let obs$ = null;
    if (this.scheduleTime.value) {
      obs$ = this.emailSendiService.sendEmail(this.emailCampaignIssue.idCampaignIssue, this.buildDate());
    } else {
      obs$ = this.emailSendiService.sendEmail(this.emailCampaignIssue.idCampaignIssue);
    }

    this.emailCreateTabManagerService.setSend(obs$);
    this.emailCreateTabManagerService.lgDone('send');
  }

  buildDate() {
    const offset = moment(this.today).utcOffset();
    let timezone;

    if (offset < 0) {
      timezone =
        '-' +
        this.today
          .toString()
          .split('-')[1]
          .split(' ')[0];
    } else {
      timezone =
        '+' +
        this.today
          .toString()
          .split('+')[1]
          .split(' ')[0];
    }
    const sendDate =
      moment(this.scheduledForm.get('sendDate').value)
        .format()
        .split('T')[0] +
      'T' +
      this.scheduledForm.get('sendTime').value +
      ':00' +
      timezone;
    return sendDate;
  }

  editSetup() {
    const obs$ = new Observable(observer => {
      observer.next(this.emailCampaignIssue);
    });
    this.emailCreateTabManagerService.setTabIsReady(obs$);
    this.emailCreateTabManagerService.setCurrentStep(1);
    this.emailCreateTabManagerService.lgDone('editSetUp');
  }

  goToEditor() {
    this.createHashIds();
    window.location.href = '/mail/mail-editor?issue=' + this.hashids.encode(this.emailCampaignIssue.idCampaignIssue) + '&new_version=true';
  }

  createHashIds() {
    this.translateService.get('pages_salt_hashids').subscribe(salt => {
      this.hashids = new Hashids(salt, 26, 'ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890');
    });
  }

  openNavigationConfemationModal() {
    this.modalConfirmNavigationModal = this.modalService.open(this.modalConfirmNavigation);

    this.modalConfirmNavigationModal.result.then(
      result => {},
      reason => {}
    );
  }

  // goToEditor() {
  // const obs$ = this.emailSendingService.useTemplate(this.emailCampaignIssue.idCampaignIssue, this.templateId);
  // this.emailCreateTabManagerService.setTabIsReady(obs$);
  //   this.emailCreateTabManagerService.lgDone('editor');
  // }

  goToTemplate() {
    this.emailCreateTabManagerService.lgDone('changeTemplate');
    this.emailCreateTabManagerService.setCurrentStep(2);
  }

  ngOnDestroy(): void {
    this.listenerNext.unsubscribe();
  }
}
